<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { AUTH_ENDPOINTS, BOT_URL, postAuth, safeRedirect, type AuthApiResponse } from '@/api/auth'
import { useResendTimer } from '@/composables/useResendTimer'
import AuthAlert from '@/components/auth/AuthAlert.vue'
import PassphraseBlock from '@/components/auth/PassphraseBlock.vue'
import TotpBlock from '@/components/auth/TotpBlock.vue'
import BackupCodesModal from '@/components/auth/BackupCodesModal.vue'
import { api } from '@/api/client'
import { markAuthChecked } from '@/router'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()

type EmailFlow = 'login' | 'register'
type Step = 'email' | 'otp' | 'pass' | 'totp'

const authMethod = ref<'email' | 'telegram'>('email')
const emailFlow = ref<EmailFlow>('login')
const step = ref<Step>('email')

const loading = ref(false)
const status = ref('')
const statusVariant = ref<'info' | 'success' | 'danger' | 'warning'>('info')

const backupCodes = ref<string[] | null>(null)
const pendingRedirect = ref('/')

const email = ref('')
const otp = ref('')
const passSetup = ref(true)
const positions = ref<number[]>([])
const totpSecret = ref('')
const totpAuth = ref('')
const resend = useResendTimer(60)

const tgOtp = ref('')
const tgStep = ref<'otp' | 'pass' | 'totp'>('otp')
const tgPassSetup = ref(true)
const tgPositions = ref<number[]>([])
const tgTotpSecret = ref('')
const tgTotpAuth = ref('')

const redirectTarget = computed(() => {
  const raw = route.query.redirect
  if (typeof raw === 'string') return safeRedirect(raw, '/')
  return '/'
})

const emailEndpoint = computed(() =>
  emailFlow.value === 'register' ? AUTH_ENDPOINTS.emailRegister : AUTH_ENDPOINTS.emailLogin,
)

async function checkAlreadyAuthed() {
  try {
    await api.get('/api/user/profile')
    markAuthChecked()
    await router.replace(redirectTarget.value)
  } catch {
    /* not logged in */
  }
}
checkAlreadyAuthed()

function setStatus(msg: string, variant: typeof statusVariant.value = 'info') {
  status.value = msg
  statusVariant.value = variant
}

function resetToEmail() {
  step.value = 'email'
  otp.value = ''
  status.value = ''
}

function finishAuth(data: AuthApiResponse) {
  if (data.backup_codes?.length) {
    backupCodes.value = data.backup_codes
    pendingRedirect.value = safeRedirect(data.redirect, redirectTarget.value)
    return
  }
  const msg = emailFlow.value === 'register' ? t('auth.js.reg_complete') : t('auth.js.login_success')
  setStatus(msg, 'success')
  setTimeout(() => {
    window.location.href = safeRedirect(data.redirect, redirectTarget.value)
  }, 800)
}

function onBackupDone() {
  backupCodes.value = null
  window.location.href = pendingRedirect.value
}

async function sendOtp() {
  loading.value = true
  status.value = ''
  const trimmed = email.value.trim()

  emailFlow.value = 'login'
  let { data, ok } = await postAuth(AUTH_ENDPOINTS.emailLogin, { step: 'send_otp', email: trimmed })

  if (!ok && data.error === 'user_not_found') {
    emailFlow.value = 'register'
    const reg = await postAuth(AUTH_ENDPOINTS.emailRegister, { step: 'send_otp', email: trimmed })
    data = reg.data
    ok = reg.ok
  }

  loading.value = false
  if (!ok) {
    const map: Record<string, string> = {
      rate_limited: t('auth.js.wait_seconds', { s: data.retry_after ?? 60 }),
      email_invalid: t('auth.js.email_invalid'),
      auth_method_disabled: t('auth.js.auth_method_disabled'),
    }
    setStatus(map[data.error ?? ''] ?? t('auth.js.generic_error'), 'danger')
    return
  }

  step.value = 'otp'
  resend.start()
  setStatus(t('auth.js.code_sent'), 'success')
}

async function resendOtp() {
  if (!resend.canResend.value) return
  loading.value = true
  const { data, ok } = await postAuth(emailEndpoint.value, { step: 'send_otp', email: email.value.trim() })
  loading.value = false
  if (!ok) {
    setStatus(
      data.error === 'rate_limited'
        ? t('auth.js.wait_seconds', { s: data.retry_after ?? 60 })
        : t('auth.js.send_error'),
      'danger',
    )
    return
  }
  resend.start()
  setStatus(t('auth.js.code_resent'), 'success')
}

async function verifyOtp() {
  loading.value = true
  status.value = ''
  const { data, ok } = await postAuth(emailEndpoint.value, {
    step: 'verify_otp',
    code: otp.value.trim(),
    email: email.value.trim(),
  })
  loading.value = false
  if (!ok) {
    setStatus(
      data.error === 'rate_limited'
        ? t('auth.js.too_many_attempts', { s: data.retry_after ?? 120 })
        : t('auth.js.otp_expired'),
      'danger',
    )
    return
  }
  if (data.next === 'done') {
    finishAuth(data)
    return
  }
  if (data.next === 'verify_totp') {
    step.value = 'totp'
    totpSecret.value = data.secret ?? ''
    totpAuth.value = data.otpauth ?? ''
    setStatus(t('auth.js.enter_proton_code'), 'success')
    return
  }
  step.value = 'pass'
  if (data.next === 'set_passphrase') {
    passSetup.value = true
    setStatus(t('auth.js.otp_accepted_set'), 'success')
  } else {
    passSetup.value = false
    positions.value = data.positions ?? []
    setStatus(t('auth.js.otp_accepted_verify'), 'success')
  }
}

async function setPassphrase(passphrase: string) {
  loading.value = true
  const { data, ok } = await postAuth(emailEndpoint.value, { step: 'set_passphrase', passphrase })
  loading.value = false
  if (!ok) {
    setStatus(t('auth.js.min4'), 'danger')
    return
  }
  passSetup.value = false
  positions.value = data.positions ?? []
  setStatus(t('auth.js.passphrase_saved_confirm'), 'success')
}

async function verifyPassphrase(letters: string[]) {
  loading.value = true
  const { data, ok } = await postAuth(emailEndpoint.value, { step: 'verify_passphrase', letters })
  loading.value = false
  if (!ok) {
    positions.value = data.positions ?? positions.value
    setStatus(t('auth.js.letters_wrong'), 'danger')
    return
  }
  if (data.next === 'done') {
    finishAuth(data)
    return
  }
  if (data.next === 'verify_totp') {
    step.value = 'totp'
    totpSecret.value = data.secret ?? ''
    totpAuth.value = data.otpauth ?? ''
    setStatus(t('auth.js.enter_proton_code'), 'success')
  }
}

async function verifyTotp(code: string) {
  loading.value = true
  const { data, ok } = await postAuth(emailEndpoint.value, { step: 'verify_totp', code })
  loading.value = false
  if (!ok) {
    setStatus(
      data.backup_codes_remaining !== undefined
        ? t('auth.js.totp_wrong_backup', { n: data.backup_codes_remaining })
        : t('auth.js.totp_wrong'),
      'danger',
    )
    return
  }
  finishAuth(data)
}

async function tgCall(body: Record<string, unknown>) {
  return postAuth(AUTH_ENDPOINTS.telegramLogin, body)
}

async function tgVerifyOtp() {
  loading.value = true
  status.value = ''
  const { data, ok } = await tgCall({ step: 'otp', code: tgOtp.value.trim() })
  loading.value = false
  if (!ok) {
    setStatus(t('auth.js.otp_invalid'), 'danger')
    return
  }
  if (data.next === 'done') {
    finishAuth(data)
    return
  }
  if (data.next === 'verify_totp') {
    tgStep.value = 'totp'
    tgTotpSecret.value = data.secret ?? ''
    tgTotpAuth.value = data.otpauth ?? ''
    setStatus(t('auth.js.enter_proton_code'), 'success')
    return
  }
  tgStep.value = 'pass'
  if (data.next === 'set_passphrase') {
    tgPassSetup.value = true
    setStatus(t('auth.js.otp_accepted_set'), 'success')
  } else {
    tgPassSetup.value = false
    tgPositions.value = data.positions ?? []
    setStatus(t('auth.js.otp_accepted_verify'), 'success')
  }
}

async function tgSetPassphrase(passphrase: string) {
  loading.value = true
  const { data, ok } = await tgCall({ step: 'set_passphrase', passphrase })
  loading.value = false
  if (!ok) {
    setStatus(t('auth.js.passphrase_too_short'), 'danger')
    return
  }
  tgPassSetup.value = false
  tgPositions.value = data.positions ?? []
  setStatus(t('auth.js.passphrase_saved'), 'success')
}

async function tgVerifyPassphrase(letters: string[]) {
  loading.value = true
  const { data, ok } = await tgCall({ step: 'verify_passphrase', letters })
  loading.value = false
  if (!ok) {
    tgPositions.value = data.positions ?? tgPositions.value
    setStatus(t('auth.js.letters_mismatch'), 'danger')
    return
  }
  if (data.next === 'done') {
    finishAuth(data)
    return
  }
  if (data.next === 'verify_totp') {
    tgStep.value = 'totp'
    tgTotpSecret.value = data.secret ?? ''
    tgTotpAuth.value = data.otpauth ?? ''
    setStatus(data.needs_setup ? t('auth.js.passphrase_confirmed_qr') : t('auth.js.passphrase_confirmed_code'), 'success')
  }
}

async function tgVerifyTotp(code: string) {
  loading.value = true
  const { data, ok } = await tgCall({ step: 'verify_totp', code })
  loading.value = false
  if (!ok) {
    setStatus(
      data.backup_codes_remaining !== undefined
        ? t('auth.js.totp_wrong_backup', { n: data.backup_codes_remaining })
        : t('auth.js.totp_invalid'),
      'danger',
    )
    return
  }
  finishAuth(data)
}

function switchToTelegram() {
  authMethod.value = 'telegram'
  tgStep.value = 'otp'
  tgOtp.value = ''
  status.value = ''
}

function switchToEmail() {
  authMethod.value = 'email'
  resetToEmail()
}
</script>

<template>
  <div class="auth-page flex min-h-screen items-center justify-center px-4 py-10">
    <div class="w-full max-w-md rounded-xl bg-white p-8 shadow">
      <!-- Email: unified login/register -->
      <template v-if="authMethod === 'email'">
        <template v-if="step === 'email'">
          <h1 class="text-center text-2xl font-extrabold uppercase tracking-wide text-primary">
            {{ t('auth.unified_title') }}
          </h1>
          <p class="mt-3 text-center text-sm text-white-dark">
            {{ t('auth.unified_subtitle') }}
          </p>

          <AuthAlert class="mt-4" :message="status" :variant="statusVariant" />

          <form class="mt-6 space-y-4" @submit.prevent="sendOtp">
            <div>
              <label class="mb-1 block text-sm font-semibold text-dark" for="auth-email">Email</label>
              <input
                id="auth-email"
                v-model="email"
                type="email"
                class="form-input"
                placeholder="you@example.com"
                autocomplete="email"
                required
              />
            </div>
            <button type="submit" class="btn btn-primary w-full uppercase tracking-wide" :disabled="loading">
              {{ t('auth.get_code') }}
            </button>
          </form>

          <p class="mt-5 text-center text-xs leading-relaxed text-white-dark">
            {{ t('auth.unified_hint') }}
          </p>

          <button
            type="button"
            class="mt-6 w-full text-center text-sm font-semibold text-primary hover:underline"
            @click="switchToTelegram"
          >
            {{ t('auth.via_telegram') }}
          </button>
        </template>

        <template v-else-if="step === 'otp'">
          <h2 class="text-lg font-bold text-dark">{{ t('auth.enter_code') }}</h2>
          <p class="mt-2 text-sm text-white-dark">
            {{ t('auth.code_sent_to') }} <strong class="text-dark">{{ email }}</strong>
          </p>
          <AuthAlert class="mt-4" :message="status" :variant="statusVariant" />
          <form class="mt-4 space-y-3" @submit.prevent="verifyOtp">
            <input
              v-model="otp"
              class="form-input text-center tracking-[0.35em]"
              maxlength="6"
              inputmode="numeric"
              autocomplete="one-time-code"
              required
            />
            <button type="submit" class="btn btn-primary w-full" :disabled="loading">{{ t('auth.next') }}</button>
          </form>
          <button
            type="button"
            class="mt-3 w-full text-xs text-white-dark disabled:opacity-50"
            :disabled="!resend.canResend.value"
            @click="resendOtp"
          >
            {{ t('auth.resend_code') }}
            <span v-if="!resend.canResend.value">({{ resend.secondsLeft }})</span>
          </button>
          <button type="button" class="mt-4 text-sm text-primary hover:underline" @click="resetToEmail">
            {{ t('auth.change_email') }}
          </button>
        </template>

        <PassphraseBlock
          v-else-if="step === 'pass'"
          :positions="positions"
          :show-setup="passSetup"
          :loading="loading"
          @set-passphrase="setPassphrase"
          @verify-letters="verifyPassphrase"
          @change-word="passSetup = true"
        />

        <TotpBlock
          v-else-if="step === 'totp'"
          :secret="totpSecret"
          :otpauth="totpAuth"
          :loading="loading"
          @verify="verifyTotp"
        />
      </template>

      <!-- Telegram (same API for login + register) -->
      <template v-else>
        <h1 class="text-center text-xl font-extrabold text-primary">{{ t('auth.via_telegram') }}</h1>
        <AuthAlert class="mt-4" :message="status" :variant="statusVariant" />

        <div v-if="tgStep === 'otp'" class="mt-4 space-y-3">
          <p class="text-sm text-white-dark">{{ t('auth.telegram_otp_hint') }}</p>
          <a :href="`${BOT_URL}?start=otp`" target="_blank" rel="noopener" class="btn btn-outline-primary w-full">
            {{ t('auth.go_to_bot') }}
          </a>
          <form class="space-y-2" @submit.prevent="tgVerifyOtp">
            <input
              v-model="tgOtp"
              class="form-input text-center"
              maxlength="6"
              inputmode="numeric"
              :placeholder="t('auth.enter_bot_code')"
              required
            />
            <button type="submit" class="btn btn-primary w-full" :disabled="loading">{{ t('auth.next') }}</button>
          </form>
        </div>

        <PassphraseBlock
          v-if="tgStep === 'pass'"
          :positions="tgPositions"
          :show-setup="tgPassSetup"
          :loading="loading"
          @set-passphrase="tgSetPassphrase"
          @verify-letters="tgVerifyPassphrase"
          @change-word="tgPassSetup = true"
        />

        <TotpBlock
          v-if="tgStep === 'totp'"
          :secret="tgTotpSecret"
          :otpauth="tgTotpAuth"
          :loading="loading"
          @verify="tgVerifyTotp"
        />

        <button type="button" class="mt-6 w-full text-sm text-primary hover:underline" @click="switchToEmail">
          {{ t('auth.back_to_email') }}
        </button>
      </template>
    </div>

    <BackupCodesModal v-if="backupCodes" :codes="backupCodes" @done="onBackupDone" />
  </div>
</template>

<style scoped>
.auth-page {
  background: #f1f5f9;
}
</style>
